import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ProjectTypes } from 'constants/enums'
import {
  firstMinedDay,
  inceptionTime,
  isRunningNote,
  priceTicker,
  toFundNavChart,
  toMiningNoteChart,
  toProductChartView,
  latestFundNav,
  latestUnderlyingPrice,
  latestBtcMined,
  returnPoints,
  toComputedValues,
  toUnderlyingStockChart,
  type ChartDenomination,
  type ComputedFields,
  type ProductChart,
  type ProductChartView,
} from 'models/productChart'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { pickContent, StoContentTypes, type GeneralContent } from 'models/projectContent'
import type { ProjectTradingData } from 'models/project'
import { FUND_UNITS_FROM } from 'models/priceChart'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'

/**
 * Off until its values are right: a running note's (BMN2) total value, in the hero chart and as
 * a Performance tab. Without its supply there is no total value, so both show the BTC mined.
 */
const SHOW_RUNNING_NOTE_TOTAL_VALUE = false

/**
 * Equity charts the underlying stock, funds their NAV, mining notes their mined BTC.
 * Each source is only requested for its product type.
 */
export function useProductChart(): SectionState<ProductChartView> {
  const { project, projectId, isPending: isProjectPending } = useStoProject()
  const type = project?.type
  const stock = useQuery(
    stoQueries.underlyingStock(
      type === ProjectTypes.EQUITY && project?.underlyingSymbol?.trim()
        ? priceTicker(project.underlyingSymbol.trim())
        : undefined,
    ),
  )
  const nav = useQuery(
    stoQueries.nav(type === ProjectTypes.FUND ? project?.secondaryAssetId?.trim() || undefined : undefined),
  )
  // Mining notes by their Liquid asset; a note without mining data gets none and no chart.
  const mined = useQuery(
    stoQueries.bmnMined(type === ProjectTypes.TRANCHES ? project?.secondaryAssetId?.trim() || undefined : undefined),
  )

  // A running mining note's notes outstanding per day, for its total issuance amount: one call
  // from its first mined day, once that is known (the endpoint is costly; BMN1 does not need it).
  const supply = useQuery(
    stoQueries.tokenSupply(
      SHOW_RUNNING_NOTE_TOTAL_VALUE && type === ProjectTypes.TRANCHES && isRunningNote(mined.data)
        ? projectId
        : undefined,
      firstMinedDay(mined.data),
    ),
  )

  // Units outstanding over time: a fund's (its total NAV), asked alongside its NAV; a note without
  // mining data's since its first sale (its issuance amount), once its mining data says it has none.
  const inception = inceptionTime(project)
  const unitsFrom =
    type === ProjectTypes.FUND
      ? FUND_UNITS_FROM
      : type === ProjectTypes.TRANCHES && mined.isSuccess && !mined.data.length && inception !== undefined
        ? new Date(inception).toISOString().slice(0, 10)
        : undefined
  const unitsOutstanding = useQuery({
    ...stoQueries.unitsOutstanding(unitsFrom ? projectId : undefined, unitsFrom),
    // The chart waits for it: a failing endpoint costs the total, not seconds of skeleton.
    retry: 1,
  })
  // Equity: the underlying stock's logo, from the trading content (the same request too).
  const content = useQuery(stoQueries.content(projectId))
  const underlyingIcon =
    type === ProjectTypes.EQUITY
      ? pickContent<ProjectTradingData>(content.data, StoContentTypes.TRADING)?.underlyingIcon
      : undefined
  // A fund annualizes its return from its start of operations, when back-office sets one.
  const startOfOperations =
    type === ProjectTypes.FUND
      ? pickContent<GeneralContent>(content.data, StoContentTypes.GENERAL)?.startOfOperations?.trim()
      : undefined
  const operationsStart = startOfOperations ? new Date(`${startOfOperations}T00:00:00Z`).getTime() : NaN

  // The product types with a chart, each from its own source; any other type has none.
  const query =
    type === ProjectTypes.FUND
      ? nav
      : type === ProjectTypes.TRANCHES
        ? mined
        : type === ProjectTypes.EQUITY
          ? stock
          : undefined
  // Stable between renders, so a hover re-render never hands the chart new points to redraw.
  const view = useMemo(() => {
    const chart =
      type === ProjectTypes.FUND
        ? toFundNavChart(project, nav.data, unitsOutstanding.data ?? undefined)
        : type === ProjectTypes.TRANCHES
          ? toMiningNoteChart(project, mined.data, supply.data, unitsOutstanding.data ?? undefined)
          : type === ProjectTypes.EQUITY
            ? toUnderlyingStockChart(project, stock.data, underlyingIcon)
            : undefined
    return chart && toProductChartView(chart, Number.isFinite(operationsStart) ? operationsStart : undefined)
  }, [
    type,
    project,
    nav.data,
    unitsOutstanding.data,
    mined.data,
    supply.data,
    stock.data,
    underlyingIcon,
    operationsStart,
  ])

  return resolveSectionState({
    // A running note waits for its supply, a fund for its units, so the issuance tab and the
    // total NAV do not appear late.
    isPending: isProjectPending || Boolean(query?.isLoading) || supply.isLoading || unitsOutstanding.isLoading,
    isError: Boolean(query?.isError),
    data: view,
    // A note charted from its supply alone has its issuance and nothing mined.
    isEmpty: (ready) => ready.chart.denominations[0].points.length === 0 && !ready.chart.note?.issuance?.length,
  })
}

/** `?currency=USD`: the currency the product's charts are shown in, one choice for the page. */
const CURRENCY_PARAM = 'currency'

/** The currency picked by the hero title's switch, lower case; undefined when none is picked. */
export function usePickedCurrency(): string | undefined {
  const [params] = useSearchParams()
  return params.get(CURRENCY_PARAM)?.toLowerCase() || undefined
}

/**
 * The chart's denomination in the currency picked by the hero title's switch (Goldstream:
 * XAUt / USD), else its default (first). In the URL, so both charts read the same choice and a
 * link keeps it; an unknown value falls back to the default.
 */
export function useChartDenomination(chart: ProductChart | undefined): ChartDenomination | undefined {
  const picked = usePickedCurrency()
  return chart?.denominations.find((item) => item.currency.toLowerCase() === picked) ?? chart?.denominations[0]
}

/** Sets the page's chart currency in place: no new history entry, the hash and scroll kept. */
export function usePickDenomination(): (currency: string) => void {
  const navigate = useNavigate()
  const { search, hash } = useLocation()
  return useCallback(
    (currency: string) => {
      const params = new URLSearchParams(search)
      params.set(CURRENCY_PARAM, currency)
      navigate({ search: `?${params}`, hash }, { replace: true, preventScrollReset: true })
    },
    [navigate, search, hash],
  )
}

/**
 * The `{{computed.…}}` metric values (Performance YTD / since inception, Fund NAV, underlying price, BTC mined), from the
 * hero chart's data in the picked currency, so a metric and the chart never disagree. Funds, equity and mining notes.
 */
const subscribeNoop = () => () => {}

export function useComputedFields(): ComputedFields {
  const { project } = useStoProject()
  const state = useProductChart()
  const chart = state.status === 'ready' ? state.data.chart : undefined
  const series = useChartDenomination(chart)
  const applies =
    project?.type === ProjectTypes.FUND ||
    project?.type === ProjectTypes.EQUITY ||
    project?.type === ProjectTypes.TRANCHES
  // A fund's NAV is server-rendered, so a ready chart reads the same on the server and the hydrating
  // render. Anything else is held on both (an equity's prices are client-only, a fund's may have
  // missed the server's budget) and settles once the page is hydrated, so the two always match.
  const isClient = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  )

  return useMemo(
    () => ({
      pending: applies && (state.status === 'loading' || (!isClient && state.status !== 'ready')),
      values:
        chart && series
          ? {
              ...toComputedValues(chart.kind, returnPoints(series), project),
              ...latestFundNav(chart, series.currency),
              ...latestUnderlyingPrice(chart),
              ...latestBtcMined(chart),
            }
          : {},
    }),
    [applies, isClient, state.status, chart, series, project],
  )
}
